// ── BitesPage.js ────────────────────────────────────────────────────────────
// Full-page "Bites" feature. Drill-down: Topics list → Shorts-style vertical
// swipe feed. Dark immersive theme (C.bitesBg), violet feature brand, per-topic
// subject accent. No emojis. User-controlled vertical scroll-snap (no auto-play).

import { useState, useEffect, useRef, useCallback } from "react";
import { C, getFontFamily } from "../constants/colors";

const VIOLET = "#8B5CF6";

// Component-scoped CSS (scrollbar hiding + the swipe hint animation).
const BITES_CSS = `
.bites-scroll::-webkit-scrollbar{display:none;}
.bites-scroll{scrollbar-width:none;-ms-overflow-style:none;}
@keyframes bites-bob{0%,100%{transform:translateY(0);opacity:.5}50%{transform:translateY(-6px);opacity:1}}
`;
function injectCss() {
  if (typeof document === "undefined" || document.getElementById("bites-css")) return;
  const el = document.createElement("style");
  el.id = "bites-css";
  el.textContent = BITES_CSS;
  document.head.appendChild(el);
}

function ChevronLeft({ size = 22, color = "#FFFFFF" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

export function BitesPage({ cls = 10, lang = "en", onBack }) {
  const ff = getFontFamily(lang);
  const isHi = lang === "hi";
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [topic, setTopic] = useState(null);   // active topic object, or null = topics list
  const [idx, setIdx] = useState(0);           // active bite index in the feed
  const feedRef = useRef(null);

  useEffect(() => { injectCss(); }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(`/data/bites/class-${cls}-history.json`)
      .then((r) => { if (!r.ok) throw new Error("HTTP " + r.status); return r.json(); })
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setErr(true); });
    return () => { cancelled = true; };
  }, [cls]);

  const openTopic = useCallback((t) => {
    setTopic(t);
    setIdx(0);
    // ensure the feed starts at the first bite
    requestAnimationFrame(() => { if (feedRef.current) feedRef.current.scrollTop = 0; });
  }, []);

  const handleBack = useCallback(() => {
    if (topic) { setTopic(null); setIdx(0); } else { onBack?.(); }
  }, [topic, onBack]);

  const onFeedScroll = useCallback((e) => {
    const elc = e.currentTarget;
    const i = Math.round(elc.scrollTop / elc.clientHeight);
    setIdx((prev) => (i !== prev ? i : prev));
  }, []);

  const root = {
    height: "100vh", width: "100%", background: C.bitesBg,
    color: "#F6F7FB", fontFamily: ff, overflow: "hidden",
    display: "flex", flexDirection: "column", position: "relative",
  };

  const backBtn = (
    <button onClick={handleBack} aria-label="Back" style={{
      position: "absolute", top: 16, left: 16, zIndex: 20,
      width: 38, height: 38, borderRadius: "50%", cursor: "pointer",
      background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.16)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <ChevronLeft />
    </button>
  );

  // ── Loading / error ──
  if (err) {
    return (
      <div style={{ ...root, alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center" }}>
        {backBtn}
        <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 14 }}>
          {isHi ? "बाइट्स लोड नहीं हो सके।" : "Could not load bites."}
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div style={{ ...root, alignItems: "center", justifyContent: "center" }}>
        {backBtn}
        <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 13 }}>
          {isHi ? "लोड हो रहा है…" : "Loading…"}
        </div>
      </div>
    );
  }

  // ── Topics list ──
  if (!topic) {
    const topics = data.topics || [];
    return (
      <div style={root}>
        {backBtn}
        <div style={{ padding: "60px 22px 10px" }}>
          <div style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: VIOLET, fontWeight: 700 }}>
            {data.subject || "History"} · {isHi ? "बाइट्स" : "Bites"}
          </div>
          <div style={{ fontSize: 24, fontWeight: 700, marginTop: 6 }}>
            {isHi ? "एक विषय चुनो" : "Pick a topic"}
          </div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", marginTop: 4 }}>
            {isHi ? "ऊपर-नीचे स्वाइप करके बाइट्स देखो।" : "Swipe up and down through bite-sized stories."}
          </div>
        </div>

        <div className="bites-scroll" style={{ flex: 1, overflowY: "auto", padding: "14px 18px 28px" }}>
          {topics.map((t, i) => {
            const accent = t.accent || VIOLET;
            const count = (t.bites || []).length;
            return (
              <div key={t.id || i} onClick={() => openTopic(t)} style={{
                position: "relative", cursor: "pointer",
                background: "#16161F", border: "1px solid rgba(255,255,255,0.07)",
                borderRadius: 18, padding: "18px 18px 16px", marginBottom: 14,
                boxShadow: "0 8px 24px rgba(0,0,0,0.30)", overflow: "hidden",
              }}>
                <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: accent }} />
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>{t.title}</div>
                    {t.subtitle && (
                      <div style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", marginTop: 4 }}>{t.subtitle}</div>
                    )}
                    <div style={{
                      display: "inline-block", marginTop: 12, fontSize: 11, fontWeight: 700,
                      letterSpacing: "0.06em", textTransform: "uppercase",
                      color: accent, background: "rgba(255,255,255,0.06)",
                      border: `1px solid ${accent}66`, borderRadius: 999, padding: "5px 11px",
                    }}>
                      {count} {isHi ? "बाइट्स" : "bites"}
                    </div>
                  </div>
                  <div style={{
                    flex: "0 0 auto", width: 46, height: 46, borderRadius: 14,
                    background: `${accent}22`, border: `1px solid ${accent}55`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill={accent}><polygon points="7 4 20 12 7 20" /></svg>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Shorts-style vertical feed for the active topic ──
  const bites = topic.bites || [];
  const accent = topic.accent || VIOLET;
  return (
    <div style={root}>
      {backBtn}

      {/* progress dots */}
      <div style={{
        position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", zIndex: 15,
        display: "flex", flexDirection: "column", gap: 7,
      }}>
        {bites.map((_, i) => (
          <span key={i} style={{
            width: 5, height: i === idx ? 16 : 5, borderRadius: i === idx ? 3 : "50%",
            background: i === idx ? accent : "rgba(255,255,255,0.25)", transition: "all 0.25s",
          }} />
        ))}
      </div>

      <div ref={feedRef} className="bites-scroll" onScroll={onFeedScroll} style={{
        height: "100%", overflowY: "auto", overflowX: "hidden",
        scrollSnapType: "y mandatory", WebkitOverflowScrolling: "touch",
      }}>
        {bites.map((b, i) => (
          <section key={i} style={{
            position: "relative", height: "100%", width: "100%",
            scrollSnapAlign: "start", scrollSnapStop: "always",
            display: "flex", flexDirection: "column",
            padding: "60px 24px 34px", overflow: "hidden",
          }}>
            {/* accent glow */}
            <div style={{
              position: "absolute", top: -120, right: -120, width: 340, height: 340, borderRadius: "50%",
              background: `radial-gradient(circle, ${accent}38, transparent 70%)`, pointerEvents: "none",
            }} />
            {/* giant faint motif */}
            <div style={{
              position: "absolute", right: -6, bottom: 78, fontSize: 120, fontWeight: 800, lineHeight: 0.9,
              color: "rgba(255,255,255,0.045)", letterSpacing: "-2px", whiteSpace: "nowrap",
              pointerEvents: "none", userSelect: "none",
            }}>{b.motif}</div>

            {/* top: type tag + counter */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", zIndex: 2 }}>
              <span style={{
                fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700,
                color: accent, background: "rgba(255,255,255,0.06)",
                border: `1px solid ${accent}80`, borderRadius: 999, padding: "5px 12px",
              }}>{b.type}</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.6)" }}>{i + 1} / {bites.length}</span>
            </div>

            {/* main reading area: lead-in question, then the story (the centrepiece) */}
            <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", zIndex: 2 }}>
              <h2 style={{ fontSize: 21, fontWeight: 700, lineHeight: 1.3, margin: "0 0 18px", maxWidth: "20ch", color: "#FFFFFF" }}>{b.hook}</h2>
              <div style={{ maxWidth: "34ch" }}>
                {(Array.isArray(b.story) ? b.story : (b.insight ? [b.insight] : [])).map((line, li) => (
                  <p key={li} style={{ fontSize: 16, lineHeight: 1.46, color: "rgba(255,255,255,0.9)", margin: "0 0 11px" }}>{line}</p>
                ))}
              </div>
            </div>

            {/* bottom: peg */}
            <div style={{ zIndex: 2 }}>
              {b.peg && (
                <span style={{
                  display: "inline-block", fontSize: 12, fontWeight: 600, color: "#F6F7FB",
                  background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 10, padding: "7px 12px",
                }}>{b.peg}</span>
              )}
            </div>

            {/* swipe hint on the first bite */}
            {i === 0 && bites.length > 1 && idx === 0 && (
              <div style={{
                position: "absolute", left: 0, right: 0, bottom: 96, textAlign: "center",
                color: "rgba(255,255,255,0.55)", fontSize: 12, letterSpacing: "0.04em", zIndex: 3,
                animation: "bites-bob 1.6s ease-in-out infinite", pointerEvents: "none",
              }}>
                {isHi ? "अगली बाइट के लिए स्वाइप करो ↑" : "swipe up for the next bite ↑"}
              </div>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}

export default BitesPage;
